/**
 * @file AdminPanel.js
 * @description View para o painel de administração, organizada em abas:
 * Equipes & Times, Ações da Liga e Administradores. Cards de equipe
 * recolhíveis e busca do Cartola isolada por equipe.
 */
import { useState, useRef } from "react";
import {
  PlusCircle,
  Trash2,
  Search,
  Loader,
  Users,
  RefreshCw,
  AlertTriangle,
  UserPlus,
  Star,
  Upload,
  Shield,
  Settings,
  ChevronDown,
  X,
} from "lucide-react";
import * as api from "../api/ligaApi";
import { useModal } from "../contexts/ModalContext";
import { RODADA_INICIAL, RODADAS_DO_TORNEIO } from "../config";

const inputBase =
  "w-full bg-campo-950 border border-campo-600 text-tinta px-3 py-2.5 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ouro focus:border-transparent transition-shadow";

const TABS = [
  { id: "equipes", label: "Equipes & Times", icon: Users },
  { id: "liga", label: "Ações da Liga", icon: Settings },
  { id: "admins", label: "Administradores", icon: Shield },
];

const AdminPanel = ({
  equipes,
  admins,
  onAddEquipe,
  onRemoveEquipe,
  onAddTime,
  onRemoveTime,
  onFetchScores,
  onUpdateAllTimes,
  onResetScores,
  onResetScoresForRound,
  onAddAdmin,
  onDeleteAdmin,
  onSetCaptain,
  onUploadLogo,
  onRefreshData,
}) => {
  const modal = useModal();
  const [activeTab, setActiveTab] = useState("equipes");
  const [newEquipeName, setNewEquipeName] = useState("");
  // Cards de equipe recolhíveis (vários abertos ao mesmo tempo)
  const [expandedEquipes, setExpandedEquipes] = useState(() => new Set());
  // Busca do Cartola isolada: aberta em uma equipe por vez
  const [searchEquipeId, setSearchEquipeId] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [rodadaAdmin, setRodadaAdmin] = useState(RODADA_INICIAL);
  const [isFetchingScores, setIsFetchingScores] = useState(false);
  const [isUpdatingTeams, setIsUpdatingTeams] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResettingRound, setIsResettingRound] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const fileInputRefs = useRef({});
  const searchInputRef = useRef(null);

  const totalTimes = equipes.reduce((acc, e) => acc + e.times.length, 0);

  const toggleEquipeExpand = (equipeId) => {
    setExpandedEquipes((prev) => {
      const next = new Set(prev);
      if (next.has(equipeId)) {
        next.delete(equipeId);
      } else {
        next.add(equipeId);
      }
      return next;
    });
  };

  const toggleSearch = (equipeId) => {
    const abrir = searchEquipeId !== equipeId;
    setSearchEquipeId(abrir ? equipeId : null);
    setQuery("");
    setSearchResults([]);
    setSearchError("");
    if (abrir) {
      // foca o campo assim que o formulário renderizar
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  };

  const handleLogoButtonClick = (equipeId) => {
    fileInputRefs.current[equipeId].click();
  };

  const handleFileChange = (e, equipeId) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("logo", file);
      onUploadLogo(equipeId, formData);
    }
    // permite selecionar o mesmo arquivo de novo
    e.target.value = "";
  };

  const handleAddEquipeSubmit = (e) => {
    e.preventDefault();
    if (!newEquipeName.trim()) return;
    onAddEquipe(newEquipeName.trim());
    setNewEquipeName("");
  };

  const handleUpdateTitulos = async (equipe, novoValor) => {
    // evita chamada à API quando o valor não mudou
    if (Number(novoValor) === (equipe.titulos || 0)) return;
    try {
      await api.updateTitulosEquipe(equipe.id, novoValor);
      if (onRefreshData) onRefreshData();
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!query) return;
    setIsSearching(true);
    setSearchError("");
    try {
      setSearchResults(await api.searchCartolaTimes(query));
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddTimeClick = async (equipeId, time) => {
    try {
      await onAddTime(equipeId, {
        nome_time: time.nome,
        nome_cartoleiro: time.nome_cartola,
        escudo_url: time.url_escudo_png,
        cartola_time_id: time.time_id,
        slug: time.slug,
      });
      setQuery("");
      setSearchResults([]);
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleFetchScoresClick = async () => {
    setIsFetchingScores(true);
    try {
      const result = await onFetchScores(rodadaAdmin);
      modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsFetchingScores(false);
    }
  };

  const handleUpdateTeamsClick = async () => {
    setIsUpdatingTeams(true);
    try {
      const result = await onUpdateAllTimes();
      modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsUpdatingTeams(false);
    }
  };

  const handleResetClick = async () => {
    setIsResetting(true);
    try {
      const result = await onResetScores();
      if (result) {
        modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetRoundClick = async () => {
    setIsResettingRound(true);
    try {
      const result = await onResetScoresForRound(rodadaAdmin);
      if (result) {
        modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResettingRound(false);
    }
  };

  const handleAddAdminSubmit = (e) => {
    e.preventDefault();
    onAddAdmin({ email: newAdminEmail, password: newAdminPassword });
    setNewAdminEmail("");
    setNewAdminPassword("");
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Cabeçalho com contexto */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-ouro">
            Painel de Administração
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            {equipes.length} equipes • {totalTimes} times na liga
          </p>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-1.5 mb-5 bg-campo-900 border border-campo-700/50 rounded-xl p-1.5 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
              activeTab === id
                ? "bg-ouro text-white"
                : "text-tinta-soft hover:text-tinta hover:bg-campo-800"
            }`}
          >
            <Icon size={15} className="flex-shrink-0" />
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{label.split(" ")[0]}</span>
          </button>
        ))}
      </div>

      {/* ── Aba: Equipes & Times ── */}
      {activeTab === "equipes" && (
        <div className="space-y-4">
          {/* Nova equipe */}
          <form
            onSubmit={handleAddEquipeSubmit}
            className="flex items-center gap-2 bg-campo-900 border border-campo-700/50 rounded-xl p-3"
          >
            <input
              type="text"
              value={newEquipeName}
              onChange={(e) => setNewEquipeName(e.target.value)}
              placeholder="Nome da nova equipe"
              className={`${inputBase} flex-grow`}
            />
            <button
              type="submit"
              disabled={!newEquipeName.trim()}
              className="flex-shrink-0 flex items-center gap-2 bg-ouro hover:bg-ouro-light disabled:bg-campo-700 disabled:text-gray-500 text-white font-bold py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              <PlusCircle size={16} />
              <span className="hidden sm:inline">Adicionar</span>
            </button>
          </form>

          {equipes.length === 0 && (
            <p className="text-center text-gray-500 text-sm py-10">
              Nenhuma equipe cadastrada.
            </p>
          )}

          {/* Cards de equipe recolhíveis */}
          {equipes.map((equipe) => {
            const isExpanded = expandedEquipes.has(equipe.id);
            const searchAberta = searchEquipeId === equipe.id;
            return (
              <div
                key={equipe.id}
                className="bg-campo-900 border border-campo-700/50 rounded-xl overflow-hidden"
              >
                {/* Cabeçalho clicável */}
                <div
                  role="button"
                  aria-expanded={isExpanded}
                  onClick={() => toggleEquipeExpand(equipe.id)}
                  className={`flex items-center justify-between px-3 sm:px-4 py-3 cursor-pointer select-none hover:bg-ouro-soft active:bg-ouro/10 transition-colors ${
                    isExpanded ? "border-b border-campo-700/40" : ""
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={api.getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                      alt=""
                      loading="lazy"
                      className="w-9 h-9 rounded-full object-contain flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <h4 className="font-bold text-tinta text-sm leading-tight truncate">
                        {equipe.nome}
                      </h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {equipe.times.length}/7 times
                        {equipe.titulos > 0 && ` • ${equipe.titulos} títulos`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <input
                      type="file"
                      ref={(el) => (fileInputRefs.current[equipe.id] = el)}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, equipe.id)}
                      accept="image/png, image/jpeg"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLogoButtonClick(equipe.id);
                      }}
                      className="p-2 rounded-lg text-gray-400 hover:text-tinta hover:bg-campo-700 transition-colors"
                      title="Fazer upload do logo"
                    >
                      <Upload size={15} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveEquipe(equipe.id);
                      }}
                      className="p-2 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Remover equipe"
                    >
                      <Trash2 size={15} />
                    </button>
                    <ChevronDown
                      size={15}
                      className={`text-gray-400 ml-1 transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Corpo expandido */}
                {isExpanded && (
                  <div className="px-3 sm:px-4 py-3 space-y-3">
                    {/* Títulos */}
                    <div className="flex items-center gap-2">
                      <Star size={13} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                      <label className="text-xs text-gray-400">Títulos:</label>
                      <input
                        type="number"
                        min="0"
                        className="w-14 bg-campo-950 text-tinta text-xs p-1.5 rounded-lg border border-campo-600 focus:border-ouro outline-none text-center"
                        defaultValue={equipe.titulos || 0}
                        onBlur={(e) => handleUpdateTitulos(equipe, e.target.value)}
                      />
                    </div>

                    {/* Times */}
                    {equipe.times.length === 0 ? (
                      <p className="text-gray-500 italic text-xs py-2 text-center">
                        Nenhum time adicionado.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {[...equipe.times]
                          .sort((a, b) => a.nome_time.localeCompare(b.nome_time))
                          .map((time) => (
                            <div
                              key={time.id}
                              className="bg-campo-950/60 px-2 py-2 rounded-lg flex items-center justify-between"
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                <button
                                  onClick={() => onSetCaptain(time.id, time.equipe_id)}
                                  className="p-1 rounded-full hover:bg-campo-700 transition-colors flex-shrink-0"
                                  title="Definir como capitão"
                                >
                                  <Star
                                    size={14}
                                    className={
                                      time.is_captain
                                        ? "text-ouro fill-ouro"
                                        : "text-gray-600"
                                    }
                                  />
                                </button>
                                <img
                                  src={api.getEscudoUrl(time.escudo_url)}
                                  alt={`Escudo do ${time.nome_time}`}
                                  loading="lazy"
                                  className="w-7 h-7 rounded-full flex-shrink-0"
                                />
                                <div className="min-w-0">
                                  <p className="font-semibold text-sm text-tinta truncate">
                                    {time.nome_time}
                                  </p>
                                  <p className="text-xs text-gray-400 truncate">
                                    {time.nome_cartoleiro}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => onRemoveTime(time.id)}
                                className="p-1.5 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0 ml-1"
                                title="Remover time"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Adicionar time do Cartola */}
                    {equipe.times.length < 7 && (
                      <div>
                        <button
                          onClick={() => toggleSearch(equipe.id)}
                          className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                            searchAberta
                              ? "bg-campo-800 border-campo-600 text-tinta-soft"
                              : "bg-emerald-600/15 border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/25"
                          }`}
                        >
                          {searchAberta ? <X size={15} /> : <PlusCircle size={15} />}
                          {searchAberta ? "Fechar busca" : "Adicionar time do Cartola"}
                        </button>

                        {searchAberta && (
                          <div className="mt-2 bg-campo-950/60 border border-campo-700/40 rounded-lg p-3">
                            <form
                              onSubmit={handleSearchSubmit}
                              className="flex items-center gap-2 mb-2"
                            >
                              <input
                                ref={searchInputRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Nome do time no Cartola..."
                                className="flex-grow bg-campo-950 border border-campo-700 text-tinta p-2 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-ouro"
                              />
                              <button
                                type="submit"
                                className="bg-ouro hover:bg-ouro-light text-white p-2 rounded-lg transition-colors flex-shrink-0"
                                disabled={isSearching}
                                title="Buscar"
                              >
                                {isSearching ? (
                                  <Loader size={16} className="animate-spin" />
                                ) : (
                                  <Search size={16} />
                                )}
                              </button>
                            </form>
                            {searchError && (
                              <p className="text-red-400 text-xs mb-2">{searchError}</p>
                            )}
                            {!isSearching && query && searchResults.length === 0 && !searchError && (
                              <p className="text-gray-500 text-xs mb-1">
                                Busque pelo nome exato do time.
                              </p>
                            )}
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {searchResults.map((time) => (
                                <div
                                  key={time.time_id}
                                  className="bg-campo-900/70 p-2 rounded-lg flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <img
                                      src={api.getEscudoUrl(time.url_escudo_png)}
                                      alt={`Escudo do ${time.nome}`}
                                      loading="lazy"
                                      className="w-7 h-7 rounded-full flex-shrink-0"
                                    />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-tinta truncate">
                                        {time.nome}
                                      </p>
                                      <p className="text-xs text-gray-400 truncate">
                                        {time.nome_cartola}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleAddTimeClick(equipe.id, time)}
                                    className="bg-emerald-600 hover:bg-emerald-500 p-1.5 rounded-lg text-white transition-colors flex-shrink-0 ml-1"
                                    title="Adicionar à equipe"
                                  >
                                    <PlusCircle size={15} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Aba: Ações da Liga ── */}
      {activeTab === "liga" && (
        <div className="space-y-4">
          {/* Sincronizar Pontuações */}
          <div className="bg-campo-900 border border-campo-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-campo-700/50">
              <h3 className="text-sm font-bold text-tinta">Sincronizar Pontuações</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Busca a pontuação oficial no Cartola após o fim da rodada
              </p>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <label
                  htmlFor="rodada-select-admin"
                  className="text-sm text-gray-400 flex-shrink-0"
                >
                  Rodada:
                </label>
                <select
                  id="rodada-select-admin"
                  value={rodadaAdmin}
                  onChange={(e) => setRodadaAdmin(Number(e.target.value))}
                  className="bg-campo-800 border border-campo-600 text-tinta p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ouro"
                >
                  {RODADAS_DO_TORNEIO.map((numero) => (
                    <option key={numero} value={numero}>
                      {numero}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={handleFetchScoresClick}
                  className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-campo-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-3 rounded-lg transition-colors text-sm"
                  disabled={isFetchingScores}
                >
                  {isFetchingScores ? (
                    <Loader size={15} className="animate-spin" />
                  ) : (
                    <RefreshCw size={15} />
                  )}
                  {isFetchingScores ? "Buscando..." : "Buscar Pontos"}
                </button>
                <button
                  onClick={handleResetRoundClick}
                  className="flex items-center justify-center gap-2 bg-red-600/15 hover:bg-red-600 border border-red-600/40 text-red-400 hover:text-white disabled:bg-campo-700 disabled:border-transparent disabled:text-gray-500 font-semibold py-2.5 px-3 rounded-lg transition-all text-sm"
                  disabled={isResettingRound}
                >
                  {isResettingRound ? (
                    <Loader size={15} className="animate-spin" />
                  ) : (
                    <Trash2 size={15} />
                  )}
                  {isResettingRound ? "Apagando..." : "Resetar Rodada"}
                </button>
              </div>
            </div>
          </div>

          {/* Atualizar Times */}
          <div className="bg-campo-900 border border-campo-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-campo-700/50">
              <h3 className="text-sm font-bold text-tinta">Atualizar Dados dos Times</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Busca os nomes e escudos mais recentes de todos os times da liga
              </p>
            </div>
            <div className="p-4">
              <button
                onClick={handleUpdateTeamsClick}
                className="w-full flex items-center justify-center gap-2 bg-ouro hover:bg-ouro-light disabled:bg-campo-700 disabled:text-gray-500 text-white font-bold py-2.5 px-4 rounded-lg transition-colors text-sm"
                disabled={isUpdatingTeams}
              >
                {isUpdatingTeams ? (
                  <Loader size={15} className="animate-spin" />
                ) : (
                  <Users size={15} />
                )}
                {isUpdatingTeams ? "Atualizando..." : "Atualizar Times"}
              </button>
            </div>
          </div>

          {/* Zona de Perigo */}
          <div className="bg-campo-900 border border-red-600/30 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-red-600/20 bg-red-500/[0.06]">
              <h3 className="flex items-center gap-2 text-sm font-bold text-red-400">
                <AlertTriangle size={14} />
                Zona de Perigo
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Apaga permanentemente todas as pontuações de todas as rodadas
              </p>
            </div>
            <div className="p-4">
              <button
                onClick={handleResetClick}
                className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-campo-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                disabled={isResetting}
              >
                {isResetting ? (
                  <Loader size={15} className="animate-spin" />
                ) : (
                  <AlertTriangle size={15} />
                )}
                {isResetting ? "Resetando..." : "Resetar Todas as Pontuações"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Aba: Administradores ── */}
      {activeTab === "admins" && (
        <div className="space-y-4">
          <div className="bg-campo-900 border border-campo-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-campo-700/50">
              <h3 className="text-sm font-bold text-tinta">Adicionar Novo Admin</h3>
            </div>
            <form onSubmit={handleAddAdminSubmit} className="p-4 space-y-3">
              <input
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="Email do novo admin"
                required
                className={inputBase}
              />
              <input
                type="password"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="Senha (mín. 6 caracteres)"
                required
                minLength="6"
                className={inputBase}
              />
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
              >
                <UserPlus size={15} />
                Adicionar Admin
              </button>
            </form>
          </div>

          <div className="bg-campo-900 border border-campo-700/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-campo-700/50">
              <h3 className="text-sm font-bold text-tinta">Admins Atuais</h3>
            </div>
            <div className="p-4 space-y-1.5">
              {admins.length === 0 && (
                <p className="text-center text-gray-500 text-xs py-3">
                  Nenhum admin cadastrado.
                </p>
              )}
              {admins.map((admin) => (
                <div
                  key={admin.id}
                  className="bg-campo-950/60 px-3 py-2 rounded-lg flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Shield size={13} className="text-gray-500 flex-shrink-0" />
                    <p className="text-sm text-tinta-soft truncate">{admin.email}</p>
                  </div>
                  <button
                    onClick={() => onDeleteAdmin(admin.id)}
                    className="p-1.5 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                    title="Remover admin"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
